import type {
  BlockConceptGroupViewDto,
  BlockConceptRowViewDto,
  BlockConceptsViewDto,
  ConceptLabels,
} from '@subscriptions/application/dtos';
import type { StoreConceptViewDto, SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { CYCLE_DAYS, getFullCycleTotal, getPeriodDays, STORE_CONCEPT_KEYS } from '@subscriptions/application/utils';
import { formatCurrencyMXN } from '@subscriptions/application/utils/formatCurrency';

type Store = SubscriptionBlockViewDto['stores'][number];

const isDiscountConcept = (concept: StoreConceptViewDto): boolean =>
  concept.key === STORE_CONCEPT_KEYS.discount || concept.value < 0;

/** The plan leads its store; everything else keeps the order the API sent. */
const byPlanFirst = (a: StoreConceptViewDto, b: StoreConceptViewDto): number =>
  Number(b.key === STORE_CONCEPT_KEYS.license) - Number(a.key === STORE_CONCEPT_KEYS.license);

const sumValues = (concepts: StoreConceptViewDto[]): number =>
  concepts.reduce((total, concept) => total + concept.value, 0);

/** A churned block is quoted for the days left of the cycle, so its charges say how much they cover. */
const formatPeriod = (concept: StoreConceptViewDto, labels: ConceptLabels, isChurned: boolean): string => {
  if (!concept.periodStart) {
    return '';
  }

  if (!concept.periodEnd) {
    return labels.coversPeriod(concept.periodStart);
  }

  const days = isChurned ? getPeriodDays(concept.periodStart, concept.periodEnd) : null;
  if (days !== null && days < CYCLE_DAYS) {
    return labels.proratedPeriod(`${concept.periodStart} – ${concept.periodEnd}`, days, CYCLE_DAYS);
  }

  return labels.coversPeriod(`${concept.periodStart} - ${concept.periodEnd}`);
};

/** The API sends only the plan name ("Pro"), so the row spells out that it is a subscription to it. */
const nameOf = (concept: StoreConceptViewDto, labels: ConceptLabels, plan: string): string => {
  if (concept.key !== STORE_CONCEPT_KEYS.license) {
    return concept.name ?? labels.conceptName(concept.key) ?? concept.key;
  }

  const planName = plan || concept.name || '';
  return planName ? labels.planName(planName) : labels.conceptName(concept.key) ?? concept.key;
};

const toRow = (
  concept: StoreConceptViewDto,
  labels: ConceptLabels,
  plan: string,
  isChurned: boolean,
): BlockConceptRowViewDto => ({
  key: concept.key,
  name: nameOf(concept, labels, plan),
  description: concept.description ?? '',
  quantityLabel: concept.quantity === null ? labels.emptyQuantity : String(concept.quantity),
  amountLabel: formatCurrencyMXN(concept.value),
  periodLabel: formatPeriod(concept, labels, isChurned),
  showMultiplier: (concept.quantity ?? 1) > 1,
  isDiscount: isDiscountConcept(concept),
});

const toGroup = (
  store: Store,
  isGrouped: boolean,
  labels: ConceptLabels,
  isChurned: boolean,
): BlockConceptGroupViewDto => {
  const discounts = store.concepts.filter(isDiscountConcept);
  const charges = [...store.concepts.filter((concept) => !isDiscountConcept(concept))].sort(byPlanFirst);

  return {
    storeUuid: store.storeUuid,
    storeName: store.storeName,
    isGrouped,
    rows: charges.map((concept) => toRow(concept, labels, store.plan, isChurned)),
    discountLabel: formatCurrencyMXN(sumValues(discounts)),
    hasDiscount: discounts.length > 0,
    subtotalLabel: formatCurrencyMXN(sumValues(store.concepts)),
  };
};

/**
 * Turns the block's stores into the rows the concepts table renders.
 *
 * Groups by store only when the block holds more than one; a single store renders as a flat list
 * whose discount row stays visible even at zero.
 */
export const mapStoresToConcepts = (
  stores: SubscriptionBlockViewDto['stores'],
  labels: ConceptLabels,
  isChurned = false,
): BlockConceptsViewDto => {
  const isGrouped = stores.length > 1;
  const groups = [...stores]
    .sort((a, b) => a.storeName.localeCompare(b.storeName))
    .map((store) => toGroup(store, isGrouped, labels, isChurned));

  const fullCycleTotal = getFullCycleTotal(stores);

  return {
    groups,
    conceptCount: groups.reduce((count, group) => count + group.rows.length, 0),
    totalLabel: formatCurrencyMXN(stores.reduce((total, store) => total + sumValues(store.concepts), 0)),
    fullCycleTotalLabel: fullCycleTotal === null ? null : formatCurrencyMXN(fullCycleTotal),
  };
};
